"use client";

import { useSearchParams } from "next/navigation";

const VERIFICATION_TYPES = {
  CONFIRMATION_TOKEN: "confirmation_token",
  VERIFICATION_CODE: "verification_code"
} as const;

type VerificationType =
  (typeof VERIFICATION_TYPES)[keyof typeof VERIFICATION_TYPES];

const isValidVerificationType = (
  type: string | null
): type is VerificationType => {
  return (
    type === VERIFICATION_TYPES.CONFIRMATION_TOKEN ||
    type === VERIFICATION_TYPES.VERIFICATION_CODE
  );
};

const isValidToken = (token: string | null): boolean => {
  return Boolean(token && token.trim().length > 0);
};

/**
 * Custom hook to check if the current page is in verification mode
 * based on the presence of valid verification parameters in URL query.
 *
 * @returns {boolean} Whether the page is in verification mode
 */
const useVerificationCode = (): boolean => {
  try {
    const searchParams = useSearchParams();
    const type = searchParams.get("type");
    const token = searchParams.get("token");

    return isValidVerificationType(type) && isValidToken(token);
  } catch {
    return false;
  }
};

export default useVerificationCode;
